import { SidebarItem, useSidebarOpenState } from '@backstage/core-components';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import type { IconElement } from '@backstage/frontend-plugin-api';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import {
  SearchModal,
  SearchModalProvider,
  SidebarSearchModal,
  searchTranslationRef,
  useSearchModal,
} from '@backstage/plugin-search';
import { RiNotification2Line, RiSearchLine } from '@remixicon/react';
import { RailButton, RailTooltip } from '@internal/plugin-waadoo-core';

/**
 * A page of the menu: a SidebarItem, with its name as tooltip in the
 * compact menu (RailTooltip).
 */
export function NavEntry(props: {
  icon: IconElement;
  href: string;
  text: string;
}) {
  const { icon, href, text } = props;
  return (
    <RailTooltip label={text}>
      <SidebarItem icon={() => icon} to={href} text={text} />
    </RailTooltip>
  );
}

/** The compact search button: plugin-search's modal, our button. */
function CompactSearch() {
  const { state, toggleModal } = useSearchModal();
  const { t } = useTranslationRef(searchTranslationRef);
  return (
    <>
      <RailButton
        aria-label={t('sidebarSearchModal.title')}
        icon={<RiSearchLine aria-hidden focusable="false" />}
        onPress={toggleModal}
      />
      <SearchModal {...state} toggleModal={toggleModal} />
    </>
  );
}

/**
 * « Rechercher »: plugin-search's own sidebar entry when the menu is
 * expanded; in the compact menu, the same modal behind a RailButton —
 * SidebarSearchModal's SidebarItem has no `to`, so it is a MUI button that
 * drops the props a tooltip needs (RailTooltip).
 */
export function NavSearch() {
  const { isOpen } = useSidebarOpenState();
  if (isOpen) {
    return <SidebarSearchModal />;
  }
  return (
    <SearchModalProvider>
      <CompactSearch />
    </SearchModalProvider>
  );
}

const NotificationsIcon = () => (
  <RiNotification2Line size={24} aria-hidden focusable="false" />
);

/**
 * « Notifications »: plugin-notifications' entry, its snackbars and title
 * counter untouched. Compact, its `renderItem`: the bell with a dot while
 * something is unread, the name as tooltip — its counter tag would not fit
 * the 72 px rail.
 */
export function NavNotifications() {
  const { isOpen } = useSidebarOpenState();
  if (isOpen) {
    return <NotificationsSidebarItem />;
  }
  return (
    <NotificationsSidebarItem
      renderItem={({ unreadCount, to, onClick }) => (
        <RailTooltip label="Notifications">
          <SidebarItem
            icon={NotificationsIcon}
            to={to}
            onClick={onClick}
            text="Notifications"
            hasNotifications={unreadCount > 0}
          />
        </RailTooltip>
      )}
    />
  );
}
